import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Grid,
  Table,
} from "semantic-ui-react";
import { isEmpty } from "lodash";
import { useForm } from "react-hook-form";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";

import {
  AccordionTitleView,
  CommonTable,
  CustomButton,
  DropDown,
  Switch,
} from "../../../../../components";
import { InputNumber } from "../../../../../components/InputText/InputNumber";
import { getLastTwentyYears, isCheckPercentageIrrigation, validatePercentageNumber } from "../../../../../utils/utils";
import PlusIcon from "../../../../../assets/commonIcon/PlusIcon";
import { CULTIVATION_TYPE, LandUsageTableHeader } from "../../../../../config/constants";
import { errorView } from "../../../../../helpers/ErrorHandler";
import { useGetLandUsage } from "../../../../../api/static-data";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";

import "../styles/CultivarForm.scss";

const LandUse = forwardRef((
  {
    currentFormData,
    currentDataType,
    currentOriginalFormData
  }: any,
  ref
) => {
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [enableUpdateButton, setEnableUpdateButton] = useState<any>(false);
  const [landUsageChange, setLandUsageChange] = useState(currentFormData?.landUsageChange || "no")
  const [landUseYearOfChange, setLandUseYearOfChange] = useState<any>("")
  const [landUsageFrom, setLandUSageFrom] = useState<any>("")
  const [landUsageTo, setLandUSageTo] = useState<any>("")
  const [currentLandUsageDetails, setCurrentLandUsageDetails] = useState<any>(currentFormData?.cultivationField?.landUsage || [])
  const [onLandUsageEdit, setOnLandUsageEdit] = useState(false)
  const [editIndex, setEditIndex] = useState<any>(null)
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [deleteValue, setDeleteValue] = useState<any>({
    item: "",
    type: "",
  });
  const originalLandUse =
    currentOriginalFormData?.cultivationField?.landUsage || [];

  useEffect(() => {
    setCurrentLandUsageDetails(currentFormData?.cultivationField?.landUsage || [])
    setLandUsageChange(currentFormData?.landUsageChange || "no")
  }, [currentFormData])

  const values = useMemo(() => {
    return {
      landUsageAllocation: "",
    }
  }, [currentFormData])

  const {
    data: landUsageData,
    isSuccess: isLandUsageSuccess,
  } = useGetLandUsage();

  const landUsage = useMemo(() => {
    if (isLandUsageSuccess) {
      let landUsage = landUsageData;
      return landUsage;
    }
  }, [landUsageData, isLandUsageSuccess]);

  const {
    formState: { errors },
    control,
    setValue,
    handleSubmit
  } = useForm({ defaultValues: values, values: values });

  useImperativeHandle(ref, () => ({
    getLandUseData: () => {
      return currentLandUsageDetails
    },
    getLanduseStatus: () => {
      return landUsageChange
    }
  }));

  const resetLandUsage = () => {
    setLandUSageFrom("");
    setLandUSageTo("");
    setLandUseYearOfChange("");
    setValue("landUsageAllocation", "");
  };

  const AddLandUsage = handleSubmit((values) => {
    if (!landUsageFrom || !landUsageTo || !landUseYearOfChange) {
      errorView("Please fill in the land usage fields");
      return;
    }
    const newEntry = {
      landUsageFrom,
      landUsageTo,
      landUseYearOfChange,
      landUsageAllocation: values.landUsageAllocation,
    };

    const updatedData = currentLandUsageDetails ? [...currentLandUsageDetails] : [];

    if (onLandUsageEdit) {
      updatedData[editIndex] = { ...updatedData[editIndex], ...newEntry };
      setOnLandUsageEdit(false);
    } else {
      updatedData.push(newEntry);
    }

    setCurrentLandUsageDetails(updatedData);
    resetLandUsage();
  });

  const onEditHandler = (item: any, index?: any) => {
    setOnLandUsageEdit(true);
    setEditIndex(index);
    setLandUSageFrom(item.landUsageFrom);
    setLandUSageTo(item.landUsageTo);
    setLandUseYearOfChange(item.landUseYearOfChange);
    setValue("landUsageAllocation", item.landUsageAllocation);
  }

  const deleteLandUse = useCallback(() => {
    setCurrentLandUsageDetails(
      currentLandUsageDetails.filter((_: any, index: number) => index !== deleteValue.index)
    )
    setVisibleDeleteModal(false);
    setDeleteValue({});
  }, [deleteValue])

  const handleClick = () => {
    setAccordionEnabled(!accordionEnabled);
  };

  return currentDataType === CULTIVATION_TYPE.Supplier ? (
    <div>
      <AccordionTitleView
        accordionEnabled={false}
        title={"Land Use Change Details"}
        plusIcon={false}
      /> <Grid>
        <Grid.Row divided>
          <Grid.Column computer={8} tablet={16} mobile={16}>
            <Grid.Column className="mt-1">
              <label className="textLabel">
                Has any part of the field management practice changed between
                land use within the last 20 years?
              </label>
              <br />
              <label className="textLabel">
                {currentOriginalFormData?.landUsageChange}
              </label>
            </Grid.Column>
            {originalLandUse.length > 0 && (
              <Grid.Column
                computer={16}
                tablet={16}
                mobile={16}
                className="landUsageTable"
              >
                <CommonTable
                  tableHeaderData={LandUsageTableHeader.filter(
                    (e: any) => e.name !== ""
                  )}
                  className="landUsageTable"
                  isSimulateView
                >
                  {originalLandUse.map((item: any, index: any) => {
                    return (
                      <Table.Row key={`landUsage_${item?.id}`}>
                        <Table.Cell>
                          <p>{item.landUseYearOfChange}</p>
                        </Table.Cell>
                        <Table.Cell>
                          <p>{item.landUsageAllocation}</p>
                        </Table.Cell>
                        <Table.Cell>
                          <p>{item?.landUsageFrom ?? ""}</p>
                        </Table.Cell>
                        <Table.Cell>
                          <p>{item?.landUsageTo ?? ""}</p>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </CommonTable>
              </Grid.Column>
            )}
          </Grid.Column>
          <Grid.Column computer={8} tablet={16} mobile={16}>
            <Grid className="mb-1">
              <Grid.Column
                computer={12}
                tablet={16}
                mobile={16}          >
                <p>
                  Has any part of the field management practice changed between land
                  use within the last 20 years?
                </p>
              </Grid.Column>
              <Grid.Column
                className="checkSwitchMain"
                computer={4}
                tablet={16}
                mobile={16}
              >
                <Switch
                  onClick={(value: any) => {
                    setLandUsageChange(value);
                  }}
                  titleOne={"No"}
                  titleTwo={"Yes"}
                  titleOneValue={"no"}
                  titleTwoValue={"yes"}
                  defaultValue={landUsageChange}
                />
              </Grid.Column>
              {landUsageChange === "yes" ? (
                <>
                  <Grid.Column computer={4} tablet={16} mobile={16}>
                    <DropDown
                      labelName="Year of change"
                      placeholder="Select Year"
                      defaultValue={landUseYearOfChange}
                      currentData={getLastTwentyYears()}
                      handleChangeState={(e: any, { value }: any) => {
                        setLandUseYearOfChange(value);
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column computer={4} tablet={16} mobile={16}>
                    <InputNumber
                      transform={{
                        input: (value: any) =>
                          isNaN(value) || value === 0 ? "" : value.toString(),
                        output: (e: any) => {
                          return e;
                        },
                      }}
                      control={control}
                      labelName={"Allocation (%)"}
                      placeholder="Allocation (%)"
                      name="landUsageAllocation"
                      enableMinus={false}
                      errors={errors?.landUsageAllocation}
                      errorMessage={"Allocation is required"}
                      required={true}
                      otherValidation={{
                        validate: {
                          valueCheck: (v: any) => validatePercentageNumber(v)
                        },
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column computer={3} tablet={16} mobile={16}>
                    <DropDown
                      labelName="Land use change - From"
                      placeholder="Select Land use change"
                      defaultValue={landUsageFrom}
                      currentData={landUsage}
                      handleChangeState={(e: any, { value }: any) => {
                        setLandUSageFrom(value);
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column computer={3} tablet={16} mobile={16}>
                    <DropDown
                      labelName="Land use change - To"
                      placeholder="Select Land use change"
                      defaultValue={landUsageTo}
                      currentData={landUsage}
                      handleChangeState={(e: any, { value }: any) => {
                        setLandUSageTo(value);
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column computer={2} tablet={2} mobile={16}>

                    {enableUpdateButton ?
                      <CustomButton
                        theme="green"
                        title={enableUpdateButton ? "Update" : ""}
                        icon={!enableUpdateButton ? "plus" : null}
                        disabled={isEmpty(landUsageFrom) || isEmpty(landUsageTo)}
                        customColumnStyle={"updateIrrgation"}
                        onClick={() => {
                          AddLandUsage();
                          setEnableUpdateButton(false);
                        }}
                      /> :
                      <CustomButton
                        theme="green"
                        customColumnStyle={"addUnitSize"}
                        icon
                        iconChild={<PlusIcon />}
                        customBase="productUnitAddButton"
                        onClick={() => {
                          AddLandUsage();
                          setEnableUpdateButton(false);
                        }}
                      />
                    }
                  </Grid.Column>
                </>
              ) : null}
              <>
                {currentLandUsageDetails?.length > 0 && (
                  <Grid.Column
                    computer={16}
                    tablet={16}
                    mobile={16}
                    className="landUsageTable"
                  >
                    <CommonTable
                      tableHeaderData={LandUsageTableHeader}
                      singleLine={false}
                      className="landUsageTable"
                    >
                      {currentLandUsageDetails?.map((item: any, index: any) => {
                        return (
                          <Table.Row key={`landUsage_${item?.id}`}>
                            <Table.Cell>
                              <p>{item.landUseYearOfChange}</p>
                            </Table.Cell>
                            <Table.Cell>
                              <p>{item.landUsageAllocation}</p>
                            </Table.Cell>
                            <Table.Cell>
                              <p>{item?.landUsageFrom ?? ""}</p>
                            </Table.Cell>
                            <Table.Cell>
                              <p>{item?.landUsageTo ?? ""}</p>
                            </Table.Cell>
                            <Table.Cell>
                              <Grid>
                                <Grid.Column
                                  computer={8}
                                  tablet={16}
                                  mobile={16}
                                >
                                  <MdModeEditOutline
                                    cursor={"pointer"}
                                    size={24}
                                    color="var(--tableEditIcon)"
                                    onClick={() => {
                                      onEditHandler(item, index);
                                      setEnableUpdateButton(true);
                                    }}
                                  />
                                </Grid.Column>
                                <Grid.Column
                                  computer={8}
                                  tablet={16}
                                  mobile={16}
                                >
                                  <MdDeleteOutline
                                    cursor={"pointer"}
                                    size={24}
                                    color="var(--tableEditIcon)"
                                    onClick={() => {
                                      setVisibleDeleteModal(true);
                                      setDeleteValue({
                                        type: "LandUsage",
                                        item: item ?? "",
                                        index: index,
                                      });
                                    }}
                                  />
                                </Grid.Column>
                              </Grid>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </CommonTable>
                  </Grid.Column>
                )}
              </>
            </Grid>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteValue({});
        }}
        approve={deleteLandUse}
        title={`Delete ${deleteValue.type}`}
        subTitle={`Are you sure you want to delete land use data?`}
      />
    </div>
  ) : null;
})

export default LandUse;
